import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon';

export interface StatCardData {
  value: string | number;
  label: string;
  subValue?: string;
  leftValue?: string;
  leftLabel?: string;
  rightValue?: string;
  rightLabel?: string;
  icon: string;
  color: string;
  variant?: 'default' | 'compact' | 'split';
  iconBgColor?: string;
  valueSize?: 'sm' | 'md' | 'lg';
}

@Component({
  selector: 'app-stat-card',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div class="bg-white rounded-lg border border-gray-200 p-6">
      <!-- Split Layout (duas colunas) -->
      <div *ngIf="variant === 'split'">
        <!-- Header com ícone e label inline -->
        <div class="flex items-center gap-3 mb-6">
          <div
            [ngClass]="color"
            class="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0"
          >
            <app-icon [name]="icon" className="w-6 h-6" *ngIf="showIcon"></app-icon>
          </div>
          <p class="text-sm text-gray-700 font-medium">{{ label }}</p>
        </div>

        <!-- Duas colunas -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-2xl font-bold text-gray-900">{{ leftValue }}</p>
            <p class="text-sm text-gray-500">{{ leftLabel }}</p>
          </div>
          <div>
            <p class="text-2xl font-bold text-gray-900">{{ rightValue }}</p>
            <p class="text-sm text-gray-500">{{ rightLabel }}</p>
          </div>
        </div>
      </div>

      <!-- Default Layout -->
      <div *ngIf="!variant || variant === 'default'">
        <div class="flex items-center justify-between mb-4">
          <div
            [ngClass]="color"
            class="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center"
          >
            <app-icon [name]="icon" className="w-6 h-6" *ngIf="showIcon"></app-icon>
          </div>
        </div>

        <!-- Value -->
        <p
          [ngClass]="{
            'text-3xl': valueSize === 'lg' || !valueSize,
            'text-2xl': valueSize === 'md',
            'text-xl': valueSize === 'sm',
          }"
          class="font-bold text-gray-900"
          [class.mb-1]="!subValue"
        >
          {{ value }}
        </p>

        <!-- Sub Value -->
        <p *ngIf="subValue" class="text-xs text-gray-500 mb-1">
          {{ subValue }}
        </p>

        <!-- Label -->
        <p class="text-sm text-gray-500">{{ label }}</p>
      </div>

      <!-- Compact Layout -->
      <div *ngIf="variant === 'compact'">
        <div class="flex items-center gap-3 mb-3">
          <div
            [ngClass]="iconBgColor || 'bg-gray-50'"
            class="w-10 h-10 rounded-lg flex items-center justify-center"
            *ngIf="showIcon"
          >
            <app-icon [name]="icon" [className]="'w-5 h-5 ' + color"></app-icon>
          </div>
          <p class="text-sm text-gray-500">{{ label }}</p>
        </div>

        <!-- Value -->
        <p
          [ngClass]="{
            'text-3xl': valueSize === 'lg',
            'text-2xl': valueSize === 'md' || !valueSize,
            'text-xl': valueSize === 'sm',
          }"
          class="font-bold text-gray-900"
        >
          {{ value }}
        </p>

        <!-- Sub Value -->
        <p *ngIf="subValue" class="text-xs text-gray-500 mt-1">
          {{ subValue }}
        </p>
      </div>
    </div>
  `,
})
export class StatCardComponent {
  @Input() value: string | number = '';
  @Input() label: string = '';
  @Input() subValue?: string;
  @Input() leftValue?: string;
  @Input() leftLabel?: string;
  @Input() rightValue?: string;
  @Input() rightLabel?: string;
  @Input() icon: string = '';
  @Input() color: string = '';
  @Input() variant: 'default' | 'compact' | 'split' = 'default';
  @Input() iconBgColor?: string;
  @Input() valueSize?: 'sm' | 'md' | 'lg' = 'md';
  @Input() showIcon: boolean = true;
}
